import {
  Autocomplete,
  FormControl,
  TextField,
  TextFieldProps,
} from '@mui/material';
import React, { SyntheticEvent, useCallback, useMemo } from 'react';

export interface MultiSelectOption<T> {
  value: T;
  label: string | number;
}

export interface MultiSelectProps<T> {
  label?: string;
  options: MultiSelectOption<T>[];
  name?: string;
  className?: string;
  isDisabled?: boolean;
  error?: string;
  value?: T[] | null;
  onChange: (value: T[]) => void;
  required?: boolean;
  placeholder?: string;
  variant?: TextFieldProps['variant'];
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  slotProps?: TextFieldProps['slotProps'];
}

const MultiSelect = <T,>({
  label,
  options,
  name,
  className,
  isDisabled = false,
  error,
  value = [],
  onChange,
  required,
  placeholder,
  variant = 'outlined',
  fullWidth,
  size,
  slotProps,
}: MultiSelectProps<T>) => {
  const selectedOptions = useMemo(
    () => options.filter(option => value?.includes(option.value)),
    [options, value],
  );

  const handleOnChange = useCallback(
    (_: SyntheticEvent, newValue: MultiSelectOption<T>[]) =>
      onChange(newValue.map(option => option.value)),
    [onChange],
  );

  const renderInput = useCallback(
    (props: TextFieldProps) => (
      <TextField
        {...props}
        required={required}
        size="small"
        label={label}
        error={!!error}
        helperText={error}
        name={name}
        slotProps={slotProps}
        variant={variant}
        disabled={isDisabled}
      />
    ),
    [label, error, isDisabled, required, variant, slotProps],
  );

  return (
    <FormControl
      required={required}
      size={size}
      placeholder={placeholder}
      className={className}
      name={name}
      disabled={isDisabled}
      variant={variant}
      fullWidth={fullWidth}
    >
      <Autocomplete
        className={className}
        multiple
        placeholder={placeholder}
        disabled={isDisabled}
        options={options}
        value={selectedOptions}
        getOptionLabel={option => String(option.label)}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        filterSelectedOptions
        onChange={handleOnChange}
        renderInput={renderInput}
        size="small"
      />
    </FormControl>
  );
};

export default MultiSelect;
